/**
 * The dashboard's Changes section (SF-5117): customer companies that started giving feedback
 * this month (none in the whole of last month), and those that gave feedback last month but none
 * so far this month. While the month is in progress the second list is "No feedback yet this
 * month" in amber, since those companies may still send some. Names open the Health tab.
 */
import { Col, Row, Table, Typography } from 'antd';
import { CompanyLink } from '../../../components/usage/CompanyLink';
import type {
  NewlyActiveRow,
  NewlyInactiveRow,
  PlatformChanges,
} from '../../../components/usage/usageClient';
import { isInProgress } from '../../../components/usage/usageProgress';
import { formatNumber } from '../../../utils/format';
import { salesChannelLabel } from '../../companies/constants';

const { Title } = Typography;

/** The section's element id, so the active companies tile can bring it into view. */
export const CHANGES_SECTION_ID = 'usage-changes';

const right = { align: 'right' as const };
// The API sends every matching customer (tens at most), so page them like biggest drops.
const pagination = { pageSize: 10, hideOnSinglePage: true, showSizeChanger: false };

const companyColumns = [
  {
    title: 'Company',
    key: 'name',
    render: (_: unknown, row: { company_id: string; name: string }) => (
      <CompanyLink companyId={row.company_id} name={row.name} />
    ),
  },
  {
    title: 'Sales channel',
    key: 'sales_channel',
    render: (_: unknown, row: { sales_channel: string | null }) =>
      salesChannelLabel(row.sales_channel),
  },
];

/** The heading over the gone quiet list, which reads differently while the month is not over. */
function goneHeading(data: Pick<PlatformChanges, 'progress'>): string {
  return isInProgress(data.progress) ? 'No feedback yet this month' : 'Gone quiet';
}

export const ChangesTables: React.FC<{ data: PlatformChanges }> = ({ data }) => {
  const inProgress = isInProgress(data.progress);
  const active = data.newly_active ?? [];
  const inactive = data.newly_inactive ?? [];
  const activeTotal = data.newly_active_total ?? active.length;
  const inactiveTotal = data.newly_inactive_total ?? inactive.length;
  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={12}>
        <Title level={5} type="success">
          {`Newly active (${formatNumber(activeTotal)})`}
        </Title>
        <Table<NewlyActiveRow>
          size="small"
          rowKey="company_id"
          aria-label="Newly active"
          pagination={pagination}
          dataSource={active}
          locale={{ emptyText: 'No newly active companies' }}
          columns={[
            ...companyColumns,
            {
              title: 'This month',
              key: 'total',
              ...right,
              render: (_, row) => formatNumber(row.total_feedback),
            },
          ]}
        />
      </Col>
      <Col xs={24} lg={12}>
        <Title level={5} type={inProgress ? 'warning' : 'danger'}>
          {`${goneHeading(data)} (${formatNumber(inactiveTotal)})`}
        </Title>
        <Table<NewlyInactiveRow>
          size="small"
          rowKey="company_id"
          aria-label={goneHeading(data)}
          pagination={pagination}
          dataSource={inactive}
          locale={{ emptyText: 'No companies' }}
          columns={[
            ...companyColumns,
            {
              title: 'Last month (whole month)',
              key: 'previous',
              ...right,
              render: (_, row) => formatNumber(row.previous_total_feedback),
            },
          ]}
        />
      </Col>
    </Row>
  );
};
